import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useClientId } from './useClientId';
import { useModules, type ModulesConfig } from '../api/endpoints/useModules';
import { buildModulesNavigation } from '../utils/modulesNavigation';
import { resolveActiveGroupIcon } from '../utils/toDiscoNav';

/**
 * The current page's parent nav-group icon, ready to hand to a component that
 * wants to echo the sidebar.
 *
 * encore-grid renders an icon in the ornament ahead of a workflow action's
 * title but has no nav tree of its own, so its `titleIcon` prop expects the
 * host to resolve one (see encore-grid#362). This is that resolution.
 *
 * Returns `undefined` when the route sits under no top-level group, or while
 * the modules request is still in flight. Pass that straight through as
 * `titleIcon` — encore-grid falls back to its own generic box, which is the
 * intended ultimate fallback. Deliberately NOT substituting a local
 * placeholder: one fallback, owned by the component that draws the ornament.
 *
 * The icon comes off the same `NavNode` tree the sidebar renders, so it cannot
 * drift from what the user sees in the nav.
 */
export function useActiveNavGroupIcon() {
  const clientId = useClientId();
  const { pathname } = useLocation();
  const { data: modulesResponse } = useModules(clientId);

  return useMemo(() => {
    const modulesData = modulesResponse?.data as ModulesConfig | undefined;
    const rootItems = modulesData?.modules ?? [];
    if (rootItems.length === 0) {
      return undefined;
    }
    const { navigation } = buildModulesNavigation(rootItems, clientId);
    return resolveActiveGroupIcon(navigation, pathname);
  }, [modulesResponse?.data, clientId, pathname]);
}
